import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { API, formatPrice, formatSize, SIZE_EU } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";

export default function ProductPage() {
    const { id } = useParams();
    const { t, lang } = useLanguage();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [size, setSize] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        setSize(null);
        setActiveImage(0);
        window.scrollTo(0, 0);
        axios
            .get(`${API}/products/${id}`)
            .then((r) => {
                setProduct(r.data);
                if (r.data.sizes.length === 1) setSize(r.data.sizes[0]);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
        axios
            .get(`${API}/products`)
            .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 2)))
            .catch(() => {});
    }, [id]);

    if (loading) {
        return (
            <main className="pt-16 md:pt-20 min-h-screen px-5 md:px-10 py-16" data-testid="product-page-loading">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 animate-pulse">
                    <div className="bg-zinc-100 aspect-[3/4]" />
                    <div className="space-y-4 pt-8">
                        <div className="h-8 bg-zinc-100 w-2/3" />
                        <div className="h-6 bg-zinc-100 w-1/4" />
                        <div className="h-24 bg-zinc-100" />
                    </div>
                </div>
            </main>
        );
    }

    if (notFound || !product) {
        return (
            <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center" data-testid="product-not-found">
                <div className="text-center">
                    <h1 className="font-display text-3xl font-bold uppercase">404</h1>
                    <Link to="/shop" className="mt-6 inline-block border border-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-black hover:text-white transition-colors" data-testid="product-404-back">
                        {t("product.backToShop")}
                    </Link>
                </div>
            </main>
        );
    }

    const name = lang === "en" && product.name_en ? product.name_en : product.name;
    const description = lang === "en" && product.description_en ? product.description_en : product.description;
    const fabric = lang === "en" && product.fabric_en ? product.fabric_en : product.fabric;
    const shipping = lang === "en" && product.shipping_en ? product.shipping_en : product.shipping;
    const images = product.images?.length ? product.images : [product.image];
    const singleSize = product.sizes.length === 1;

    const handleAdd = () => {
        if (!size) {
            toast.error(t("quickview.selectSize"));
            return;
        }
        addItem({ ...product, name }, size);
        toast.success(`${name} ${t("quickview.added")}`);
    };

    return (
        <main className="pt-16 md:pt-20 min-h-screen" data-testid="product-page">
            <div className="px-5 md:px-10 py-8 md:py-12">
                <Link
                    to="/shop"
                    data-testid="product-back-link"
                    className="group inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 hover:text-black transition-colors"
                >
                    <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
                    {t("product.backToShop")}
                </Link>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    {/* Gallery */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="bg-zinc-100 aspect-[3/4] overflow-hidden">
                            <motion.img
                                key={images[activeImage]}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.4 }}
                                src={images[activeImage]}
                                alt={name}
                                className="w-full h-full object-cover object-top"
                                data-testid="product-main-image"
                            />
                        </div>
                        {images.length > 1 && (
                            <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar" data-testid="product-thumbnails">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImage(i)}
                                        aria-label={`Foto ${i + 1}`}
                                        data-testid={`product-thumb-${i}`}
                                        className={`w-20 h-24 flex-shrink-0 overflow-hidden border-2 bg-zinc-100 transition-colors duration-200 ${
                                            activeImage === i ? "border-black" : "border-transparent"
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.7 }}
                        className="lg:py-6"
                    >
                        <h1 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight" data-testid="product-title">
                            {name}
                        </h1>
                        <p className="font-display text-2xl font-bold mt-4" data-testid="product-price">
                            {formatPrice(product.price)}
                        </p>

                        <p className="text-base text-zinc-600 leading-relaxed mt-6" data-testid="product-description">
                            {description}
                        </p>

                        {product.sold_out ? (
                            <div className="mt-8 border-2 border-black p-6" data-testid="product-sold-out">
                                <p className="font-display text-2xl font-bold uppercase tracking-tight">
                                    {t("product.soldOut")}
                                </p>
                                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                                    {t("product.soldOutNote")}
                                </p>
                            </div>
                        ) : (
                            <>
                        <div className="mt-8">
                            <p className="text-xs tracking-[0.25em] uppercase font-semibold mb-3">
                                {t("quickview.size")}{" "}
                                {singleSize && (
                                    <span className="text-zinc-400 normal-case tracking-normal">
                                        — {t("quickview.onlySize").replace("{size}", formatSize(product.sizes[0]))}
                                    </span>
                                )}
                            </p>
                            <div className="flex gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSize(s)}
                                        data-testid={`product-size-${s}`}
                                        className={`w-14 h-14 border flex flex-col items-center justify-center transition-colors duration-200 ${
                                            size === s ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"
                                        }`}
                                    >
                                        <span className="text-sm font-semibold leading-none">{s}</span>
                                        {SIZE_EU[s] && (
                                            <span className={`text-[9px] mt-1 leading-none ${size === s ? "text-zinc-300" : "text-zinc-400"}`}>
                                                EU {SIZE_EU[s]}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handleAdd}
                            data-testid="product-add-to-cart"
                            className="mt-8 w-full bg-black text-white py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-zinc-800"
                        >
                            {t("quickview.add")}
                        </button>
                            </>
                        )}

                        <div className="mt-10 divide-y divide-zinc-200 border-y border-zinc-200">
                            <div className="py-5" data-testid="product-fabric">
                                <p className="text-xs tracking-[0.25em] uppercase font-semibold">{t("product.fabric")}</p>
                                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{fabric}</p>
                            </div>
                            <div className="py-5" data-testid="product-shipping">
                                <p className="text-xs tracking-[0.25em] uppercase font-semibold">{t("product.shipping")}</p>
                                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{shipping}</p>
                            </div>
                        </div>

                        <p className="mt-6 text-xs text-zinc-400 tracking-wider">{t("product.handmadeNote")}</p>

                        <Link
                            to="/#su-commissione"
                            data-testid="product-custom-cta"
                            className="group mt-8 flex items-center justify-between border border-zinc-200 p-5 transition-colors duration-300 hover:bg-black hover:text-white hover:border-black"
                        >
                            <span className="text-sm font-semibold">{t("product.customCta")}</span>
                            <span className="text-xs tracking-[0.2em] uppercase text-zinc-400 group-hover:text-zinc-300 transition-colors">
                                {t("product.customCtaLink")} →
                            </span>
                        </Link>
                    </motion.div>
                </div>

                {related.length > 0 && (
                    <div className="mt-24" data-testid="related-products">
                        <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight mb-10">
                            {t("product.alsoLike")}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-3xl">
                            {related.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
